import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PharmacyLayout } from './PharmacyLayout';
import { ValidatedNumberInput } from '../../components/ui/validated-number-input';
import { DatePicker } from '../../components/ui/DatePicker';
import {
  usePharmacyPurchaseOrder,
  useCreatePharmacyGoodsReceipt,
} from '../../services/httpServices/queries';
import { Loader2, ArrowLeft, Truck, ShoppingCart } from 'lucide-react';
import { toast } from 'sonner';

interface ReceiveLine {
  poItemId: string;
  drugId: string;
  drugName: string;
  orderedQuantity: number;
  alreadyReceived: number;
  quantityReceived: number;
  unitCost: number;
  batchNumber: string;
  expiryDate: string;
}

export function PharmacyReceive() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: poData, isLoading } = usePharmacyPurchaseOrder(id);
  const createMutation = useCreatePharmacyGoodsReceipt();
  const order = poData?.data;

  const [receivedDate, setReceivedDate] = useState(new Date().toISOString().slice(0, 10));
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [createInvoice, setCreateInvoice] = useState(true);
  const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<ReceiveLine[]>([]);

  // Prefill from the purchase order once loaded
  useEffect(() => {
    if (!order) return;
    setLines(
      (order.items || []).map((item) => ({
        poItemId: item.id,
        drugId: item.drugId,
        drugName: item.drug?.name || item.drugId,
        orderedQuantity: Number(item.orderedQuantity),
        alreadyReceived: Number(item.receivedQuantity),
        quantityReceived: Math.max(
          0,
          Number(item.orderedQuantity) - Number(item.receivedQuantity),
        ),
        unitCost: Number(item.unitCost),
        batchNumber: item.batchNumber || '',
        expiryDate: item.expiryDate ? String(item.expiryDate).slice(0, 10) : '',
      })),
    );
    if (order.invoiceNumber) setInvoiceNumber(order.invoiceNumber);
  }, [order]);

  const receiptTotal = useMemo(
    () =>
      lines.reduce(
        (sum, l) => sum + Number(l.quantityReceived) * Number(l.unitCost),
        0,
      ),
    [lines],
  );

  const updateLine = (index: number, patch: Partial<ReceiveLine>) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!order) return;
    const valid = lines.filter((l) => Number(l.quantityReceived) > 0 && l.batchNumber);
    if (valid.length === 0) {
      toast.error('Enter a received quantity and batch number for at least one item');
      return;
    }

    try {
      await createMutation.mutateAsync({
        poId: order.id,
        supplierId: order.supplierId,
        receivedDate: receivedDate || undefined,
        invoiceNumber: invoiceNumber || undefined,
        createInvoice,
        notes: notes || undefined,
        items: valid.map((l) => ({
          poItemId: l.poItemId,
          drugId: l.drugId,
          quantityReceived: Number(l.quantityReceived),
          unitCost: Number(l.unitCost),
          batchNumber: l.batchNumber,
          expiryDate: l.expiryDate || undefined,
        })),
      });
      toast.success('Goods receipt recorded');
      navigate(`/pharmacy/purchases/${order.id}`);
    } catch {
      // handled globally
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-border rounded-lg bg-background text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary';

  const back = (
    <button
      onClick={() => navigate(order ? `/pharmacy/purchases/${order.id}` : '/pharmacy/purchases')}
      className="text-sm text-text-muted hover:text-text flex items-center gap-1.5"
    >
      <ArrowLeft size={16} /> Back to purchase orders
    </button>
  );

  // Receiving now always happens against a purchase order.
  if (!id) {
    return (
      <PharmacyLayout title="Receive Goods">
        {back}
        <div className="bg-surface rounded-xl border border-border p-10 text-center shadow-sm">
          <ShoppingCart className="w-10 h-10 text-text-muted mx-auto mb-3" />
          <p className="text-sm text-text-muted">
            Goods are received against a purchase order. Open a purchase order and choose{' '}
            <span className="text-text font-medium">Receive Goods</span>.
          </p>
          <button
            onClick={() => navigate('/pharmacy/purchases')}
            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm font-medium"
          >
            Go to Purchase Orders
          </button>
        </div>
      </PharmacyLayout>
    );
  }

  if (isLoading || !order) {
    return (
      <PharmacyLayout title="Receive Goods">
        {back}
        <div className="py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
        </div>
      </PharmacyLayout>
    );
  }

  return (
    <PharmacyLayout
      title={`Receive ${order.poNumber}`}
      description={`Record the medicines received from ${order.supplier?.name || 'supplier'}`}
    >
      {back}

      <form onSubmit={submit} className="space-y-4">
        <div className="bg-surface rounded-xl border border-border p-4 shadow-sm grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-text mb-1">Supplier</label>
            <div className={`${inputClass} bg-background/50 text-text-muted`}>
              {order.supplier?.name || '—'}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Received Date</label>
            <DatePicker value={receivedDate} onChange={setReceivedDate} className={inputClass} />
          </div>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Supplier Invoice No.</label>
            <input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              className={inputClass}
              placeholder="Optional"
            />
          </div>
        </div>

        <div className="bg-surface rounded-xl border border-border shadow-sm">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Truck size={16} className="text-primary" />
              <span className="font-medium text-text">Items to Receive</span>
            </div>
            <span className="text-sm text-text-muted">
              Receipt total <span className="font-semibold text-text">{receiptTotal.toFixed(2)}</span>
            </span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Drug</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Ordered</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Already Received</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Qty Now</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Unit Cost</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Batch No. *</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-text-muted uppercase">Expiry</th>
                  <th className="px-3 py-2 text-right text-xs font-medium text-text-muted uppercase">Line Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {lines.map((line, index) => (
                  <tr key={line.poItemId} className={line.quantityReceived === 0 ? 'opacity-60' : ''}>
                    <td className="px-3 py-2 min-w-[200px] text-text">{line.drugName}</td>
                    <td className="px-3 py-2 text-right text-text-muted">{line.orderedQuantity}</td>
                    <td className="px-3 py-2 text-right text-text-muted">{line.alreadyReceived}</td>
                    <td className="px-3 py-2 w-28">
                      <input
                        type="number"
                        min={0}
                        max={line.orderedQuantity - line.alreadyReceived}
                        value={line.quantityReceived}
                        onChange={(e) =>
                          updateLine(index, { quantityReceived: Number(e.target.value) })
                        }
                        className={`${inputClass} text-right`}
                      />
                    </td>
                    <td className="px-3 py-2 w-28">
                      <ValidatedNumberInput
                        value={line.unitCost}
                        onChange={(v) => updateLine(index, { unitCost: v })}
                        className={`${inputClass} text-right`}
                      />
                    </td>
                    <td className="px-3 py-2 w-40">
                      <input
                        value={line.batchNumber}
                        onChange={(e) => updateLine(index, { batchNumber: e.target.value })}
                        className={inputClass}
                        placeholder="Batch number"
                      />
                    </td>
                    <td className="px-3 py-2 w-40">
                      <DatePicker
                        value={line.expiryDate}
                        onChange={(value) => updateLine(index, { expiryDate: value })}
                        className={inputClass}
                      />
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-text">
                      {(Number(line.quantityReceived) * Number(line.unitCost)).toFixed(2)}
                    </td>
                  </tr>
                ))}
                {lines.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-text-muted">
                      This purchase order has no items.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-surface rounded-xl border border-border p-4 shadow-sm space-y-3">
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={createInvoice}
              onChange={(e) => setCreateInvoice(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm text-text">
              Generate supplier bill automatically
              <span className="block text-xs text-text-muted">
                Creates a payable from this receipt using the received quantities and unit costs.
              </span>
            </span>
          </label>
          <div>
            <label className="block text-sm font-medium text-text mb-1">Notes</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className={inputClass}
              placeholder="Optional"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(`/pharmacy/purchases/${order.id}`)}
            className="px-4 py-2 text-sm text-text-muted hover:text-text flex items-center gap-1.5"
          >
            <ArrowLeft size={16} /> Cancel
          </button>
          <button
            type="submit"
            disabled={createMutation.isPending}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark text-sm font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {createMutation.isPending && <Loader2 size={16} className="animate-spin" />}
            <Truck size={16} /> Record Receipt
          </button>
        </div>
      </form>
    </PharmacyLayout>
  );
}
